import { BaseChartDirective } from 'ng2-charts';
import { BudgetHistoryParams, BudgetObject, DateRange, BudgetHistoryGroup, GroupTypeRecord, BudgetHistoryChartData, FilterMonth, FilterYear } from '../../models/budget.model';
import { BudgetService } from '../../services/budget/budget.service';
import { BudgetSetupService } from '../cv-admin-budget-setup/service/budget-setup.service';
import { ChartConfiguration, ChartDataset, ChartOptions } from 'chart.js';
import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { formatDate } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';
import moment from 'moment';
import { MenuItem } from '@niceltd/sol/floating-menu/src/lib/menu-items';
import { TypeFormats } from 'src/shared/types/exportItem';

@Component({
    selector: 'cv-admin-budget-history',
    templateUrl: './cv-admin-budget-history.component.html',
    styleUrls: ['./cv-admin-budget-history.component.scss']
})
export class CvAdminBudgetHistoryComponent implements OnInit, OnDestroy {

    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    isLoading = false;
    isChartLoading = false;
    budgetHistoryForm: FormGroup;
    groups: BudgetHistoryGroup[];
    numberOfYears = 3;
    selectedGroup: GroupTypeRecord = {} as GroupTypeRecord;
    fileName: string = "";
    historyChartData = {
        labels: [],
        datasets: [],
        dateRange: '',
    };
    chartStyleConfig = {
        fontFamily: {
            family: 'Open Sans'
        },
        axisTitleStyle: {
            size: 13,
            weight: '600'
        },
        ticksStyle: {
            size: 12,
            weight: '400'
        },
        legendStyle: {
            size: 13,
            weight: '400'
        },
        tooltipStyle: {
            size: 13,
        },
        fontColor: '#3F5C69',
        colorLineChart: '#EFA716',
        colorBarChart: '#5261FF',
    };
    historyChartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    font: {
                        ...this.chartStyleConfig.fontFamily,
                        ...this.chartStyleConfig.axisTitleStyle
                    },
                    color: this.chartStyleConfig.fontColor
                },
                ticks: {
                    font: {
                        ...this.chartStyleConfig.fontFamily,
                        ...this.chartStyleConfig.ticksStyle
                    },
                    color: this.chartStyleConfig.fontColor
                }
            },
            y: {
                display: true,
                title: {
                    display: true,
                    font: {
                        ...this.chartStyleConfig.fontFamily,
                        ...this.chartStyleConfig.axisTitleStyle
                    },
                    color: this.chartStyleConfig.fontColor
                },
                ticks: {
                    font: {
                        ...this.chartStyleConfig.fontFamily,
                        ...this.chartStyleConfig.ticksStyle
                    },
                    color: this.chartStyleConfig.fontColor,
                    callback: (tickValue, index, ticks) => {
                        const formattedValue = tickValue.toLocaleString(CVCultureName);
                        return formattedValue;
                    },
                }
            }
        },
        plugins: {
            legend: {
                display: true,
                labels: {
                    boxWidth: 12,
                    padding: 8,
                    font: {
                        ...this.chartStyleConfig.fontFamily,
                        ...this.chartStyleConfig.legendStyle
                    },
                    boxPadding: 24,
                    color: this.chartStyleConfig.fontColor,
                },
                onClick: (e, legendItem, legend) => {
                    const chartInstance = legend.chart;
                    const datasetIndex = legendItem.datasetIndex;
                    const meta = chartInstance.getDatasetMeta(datasetIndex);
                    meta.hidden = !meta.hidden;
                    chartInstance.options.animation = {
                        duration: 0,
                    };
                    chartInstance.update();
                },
            },
            datalabels: {
                display: false,
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                position: 'nearest',
                bodyFont: {
                    ...this.chartStyleConfig.fontFamily,
                    ...this.chartStyleConfig.tooltipStyle
                },
                callbacks: {
                    title: (tooltipItems) => {
                        const tooltipTitle = tooltipItems[0]?.label;
                        if (this.isYear && tooltipTitle) {
                            const monthName = this.months.find(month => month.shortMonthName === tooltipTitle);
                            return monthName?.localizedName;
                        }
                        return tooltipTitle;
                    },
                    label: (tooltipItem) => {
                        const coinsAwardedLabel = this.getLocalizationStringBudgetHistory('Coins_Awarded', 'Coins Awarded');
                        const coinAwardedLabel = this.getLocalizationStringBudgetHistory('Coin_Awarded', 'Coin Awarded');
                        const coinValue = tooltipItem.formattedValue.replace(/\D/g, '');
                        const datasetLabel = tooltipItem.dataset.label === coinsAwardedLabel ? (Number(coinValue) === 1 ? coinAwardedLabel : coinsAwardedLabel) : tooltipItem.dataset.label;
                        const formmattedCoinValue = Number(coinValue).toLocaleString(CVCultureName);
                        return `${datasetLabel}: ${formmattedCoinValue}`;
                    },
                },
            }
        },
        animation: {
            duration: 1000,
        },
        layout: {
            padding: {
                right: 5,
            },
        }
    };
    historyChartConfig: ChartConfiguration<'bar'> = {
        type: 'bar',
        data: this.historyChartData,
        options: this.historyChartOptions,
    };

    defaultMenuOptions: MenuItem[] = [
        {
          label: "PNG",
          svgIconName: "icon-png",
          callbackFn: this.onExportClick.bind(this),
        }
    ];

    backgroundcolor = {
        id: 'custom_canvas_background_color',
        beforeDraw: (chart) => {
          const ctx = chart.canvas.getContext('2d');
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = '#F5F8FA';
          ctx.fillRect(0, 0, chart.canvas.width, chart.canvas.height);
          ctx.restore();
        }
    }
    
    noData = {
        id: 'custom_canvas_background_color',
        afterDraw: (chart) => {
          const { datasets } = chart.data;
          let hasData = false;
    
          for (let dataset of datasets) {
            if (dataset.data.length > 0 && dataset.data.some(item => item !== 0)) {
              hasData = true;
              break;
            }
          }

          if (!hasData) {
            const { chartArea: { left, top, right, bottom }, ctx } = chart;
            const centerX = (left + right) / 2;
            const centerY = (top + bottom) / 2;
            //This is only to simulate lines in the image. 
            chart.clear();
            ctx.save();
    
            ctx.fillStyle = 'white';
            ctx.fillRect(left, top, right - left, bottom - top);
    
            ctx.beginPath();
            ctx.strokeStyle = '#ddd';
            ctx.lineWidth = 1;
    
            for (let y = top; y <= bottom; y += (bottom - top) / 10) {
              ctx.moveTo(left, y);
              ctx.lineTo(right, y);
            }
    
            for (let x = left; x <= right; x += (right - left) / 10) {
              ctx.moveTo(x, top);
              ctx.lineTo(x, bottom);
            }
            ctx.stroke();
    
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#000';
            ctx.font = '12px Arial';
    
            ctx.fillText(this.getLocalizationStringBudgetHistory("Chart_No_Data", "No data available"), centerX, centerY);
    
            ctx.restore();
          }
        }
      }
    chartConfig = null;

    protected readonly dateRange = DateRange;
    private budgetHistorySubscription: Subscription = new Subscription();

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private budgetService: BudgetService,
        private budgetSetupService: BudgetSetupService,
        private cvLocalizationService: CVLocalizationService,
        private fb: FormBuilder,
    ) {
        this.budgetHistoryForm = this.fb.group({
            dateRange: [],
            month: [],
            year: [],
            group: []
        });
        this.canvasRef = new ElementRef(null);
    }

    ngAfterViewInit(): void {
        this.canvasRef = this.canvasRef || new ElementRef(null);
    }

    get sectionTitle(): string {
        return this.getLocalizationStringBudgetHistory('Budget_History', 'Budget History');
    }

    get dayTabName(): string {
        return this.getLocalizationStringBudgetHistory('Day', 'Day');
    }

    get monthsTabName(): string {
        return this.getLocalizationStringBudgetHistory('Months', 'Months');
    }

    get years(): FilterYear[] {
        const currentYear = moment().year();
        return Array.from({ length: this.numberOfYears }, (_, i) => ({
            id: currentYear - i,
            value: currentYear - i
        }));
    }

    get months(): FilterMonth[] {
        const monthNameKeys = ['Mth_January', 'Mth_February', 'Mth_March', 'Mth_April', 'Mth_May',
            'Mth_June', 'Mth_July', 'Mth_August', 'Mth_September', 'Mth_October', 'Mth_November', 'Mth_December'];
        return monthNameKeys.map((month, index) => {
            const defaultMonthValue = month.replace('Mth_', '');
            return {
                id: index + 1,
                value: defaultMonthValue,
                localizedName: this.getLocalizationStringControls(month, defaultMonthValue),
                shortMonthName: formatDate(moment().month(index).toDate(), 'MMM', this.locale)
            };
        });
    }

    get groupLabel(): string {
        return this.selectedGroup?.LocalizedName ?? this.getLocalizationStringBudgetHistory('Company', 'Company');
    }

    get isYear(): boolean {
        return this.budgetHistoryForm.get('dateRange')?.value === this.dateRange.YEAR;
    }

    async ngOnInit() {     
        await this.loadLocalization();
        this.loadData();
    }

    ngOnDestroy(): void {
        if (this.budgetHistorySubscription) {
            this.budgetHistorySubscription.unsubscribe();
        }
    }

    loadData() {
        this.setDefaultFormData();
        const budget = this.budgetService.budgetList$.subscribe(budgetList => {
            this.groups = this.formatGroups(budgetList);
        });
        const selectedGrp = this.budgetSetupService.budgetSetup$
            .pipe(distinctUntilChanged((prev, curr) => prev.selectedGroup?.GroupTypeName === curr.selectedGroup?.GroupTypeName))
            .subscribe(({ selectedGroup }) => {
                this.selectedGroup = selectedGroup;
                this.loadDefaultHistoryChart();
            });
        this.budgetHistorySubscription.add(budget);
        this.budgetHistorySubscription.add(selectedGrp);
    }

    setDefaultFormData() {
        this.budgetHistoryForm.patchValue({
            dateRange: this.dateRange.MONTH,
            month: this.months.find(month => month.id === moment().month() + 1),
            year: this.years.find(year => year.id === moment().year()),
        });
    }

    loadDefaultHistoryChart() {
        this.budgetHistoryForm.patchValue({
            group: this.groups?.length > 0 ? this.groups[0] : [],
        });
        this.loadBudgetHistory();
    }

    async loadBudgetHistory() {
        this.isLoading = true;
        try {
            const historyParams = this.historyParams();
            if (historyParams.GroupID === 0 && historyParams.GroupLevel === 0) {
                return;
            }
            const coinHistory = await this.budgetService.loadBudgetHistory(historyParams);
            const charData: BudgetHistoryChartData = {
                labels: Object.keys(coinHistory).map(key => this.createLabel(key)),
                awarded: Object.values(coinHistory).map(history => history.CoinsAwarded),
                spent: Object.values(coinHistory).map(history => history.CoinsSpent),
                dateRange: this.budgetHistoryForm.get('dateRange')?.value ?? DateRange.MONTH,
            };
            this.createChart(charData);
        } catch (error) {
            console.error('Failed to create the chart for the budget history', error);
        } finally {
            this.isLoading = false;
        }
    }

    async loadLocalization() {
        await this.cvLocalizationService.GetSection('Controls');
        await this.cvLocalizationService.GetSection('Budget_History');
        this.fileName = await this.cvLocalizationService.GetLocalText("Budget", "Budget_History", "Budget History");
    }

    getLocalizationStringControls(key: string, defaultValue: string) {
        return this.cvLocalizationService.getCachedText('Controls', key, defaultValue);
    }

    getLocalizationStringBudgetHistory(key: string, defaultValue: string) {
        return this.cvLocalizationService.getCachedText('Budget_History', key, defaultValue);
    }

    onSelectGroup() {
        if (this.isComponentDisabled()) {
            return;
        }
        const { group } = this.budgetHistoryForm.value;
        if (!group) {
            return;
        }
        this.loadBudgetHistory();
    }

    async onExportClick() {
        const body = {
            format: "png" as TypeFormats,
        }
        
        const canvas = await this.exportPNG();
        const imageDataUrl = canvas.toDataURL('image/png');
        const fileName = `${this.fileName}PNG_${moment().format('DD MMM,YYYY h[h]mm[m]ss[s] A')}.png`;

        const link = document.createElement('a');
        link.href = imageDataUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        await this.budgetService.exportBudgetHistory(body);
        return imageDataUrl;
    }

    exportPNG(): Promise<HTMLCanvasElement> {
        //It is necessary to draw the chart for downloading.
        //If it exists within the same component, it is only taken from the state of the chart.
        return new Promise((resolve, reject) => {
          this.chartConfig = {
            type: 'bar',
            data: this.historyChartData,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              scaleShowVerticalLines: false,
              scales: {
                x: {
                  display: true,
                  title: {
                    display: true,
                    text: this.historyChartData.dateRange == 'Year' ? this.getLocalizationStringBudgetHistory("Months_of_the_Year", "Months of the Year") : this.getLocalizationStringBudgetHistory("Days_of_the_Month", "Days of the Month")
                  }
                },
                y: {
                  display: true,
                  title: {
                    display: true,
                    text: this.getLocalizationStringBudgetHistory("Amount_Coins_Awarded", "Amount of Coins Awarded")
                  }
                }
              },
              plugins: {
                legend: {
                  display: true,
                  position: 'top',
                  labels: {
                    fontColor: 'black',
                    fontSize: 12,
                  }
                }
              }
            },
            plugins: [this.backgroundcolor, this.noData],
    
          } as ChartConfiguration;
    
          setTimeout(() => {
            const canvas = this.canvasRef.nativeElement;
            const ctx = canvas.getContext('2d');
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
            resolve(canvas);
          }, 800);
        });
    }

    formatGroups(budgetList: BudgetObject[]): BudgetHistoryGroup[] {
        return budgetList?.map((budget) => ({
            GroupLevel: budget.GroupLevel,
            GroupLevelName: budget.GroupLevelName,
            BudgetID: budget.BudgetID,
            EmpGroupID: budget.EmpGroupID,
        })).sort((a, b) => a.GroupLevelName.localeCompare(b.GroupLevelName, undefined, { sensitivity: 'base' }));
    }

    historyParams(): BudgetHistoryParams {
        const { dateRange, month, year, group } = this.budgetHistoryForm.value;
        return {
            budgetSettings: {
                DateRange: dateRange,
                Month: month.id - 1,
                Year: year.id,
            },
            GroupID: group?.EmpGroupID ?? 0,
            GroupLevel: group?.GroupLevel ?? 0,
        };
    }

    createLabel(key: string): string {
        if (this.isYear) {
            const monthData = this.months.find(month => month.value === key);
            return monthData.shortMonthName;
        } else {
            //specify the endpoint date format for Moment
            const parsedDate = moment(key, 'M/D/YYYY', true);
            const dayOfTheMonth = parsedDate.isValid() ? parsedDate.format('D') : '0';
            return dayOfTheMonth;
        }
    }

    createChart(data: BudgetHistoryChartData) {
        this.isChartLoading = true;
        const monthsLocalization = this.getLocalizationStringBudgetHistory('Months', 'Months');
        const daysLocalization = this.getLocalizationStringBudgetHistory('Day', 'Day');

        const historyLineChart: ChartDataset<'line'> = {
            type: 'line',
            label: this.getLocalizationStringBudgetHistory('Budget_Assigned', 'Budget Assigned'),
            data: data.spent,
            fill: false,
            borderColor: this.chartStyleConfig.colorLineChart,
            backgroundColor: this.chartStyleConfig.colorLineChart,
            borderWidth: 1,
            pointRadius: 4,
            pointBackgroundColor: this.chartStyleConfig.colorLineChart,
            pointBorderColor: this.chartStyleConfig.colorLineChart,
        };
        
        const historyBarChart: ChartDataset<'bar'> = {
            type: 'bar',
            label: this.getLocalizationStringBudgetHistory('Coins_Awarded', 'Coins Awarded'),
            data: data.awarded,
            backgroundColor: this.chartStyleConfig.colorBarChart,
            borderColor: this.chartStyleConfig.colorBarChart,
            maxBarThickness: 36,
        };

        const dataset = [];
        if (this.isYear) {
            dataset.push(historyLineChart);
        }
        dataset.push(historyBarChart);

        this.historyChartData.labels = data.labels;
        this.historyChartData.datasets = dataset;
        this.historyChartData.dateRange = data.dateRange;
        this.historyChartOptions.scales.x.title.text = this.isYear ? monthsLocalization : daysLocalization;
        this.historyChartOptions.scales.y.title.text = this.getLocalizationStringBudgetHistory('Coins_Awarded', 'Coins Awarded');
        this.isChartLoading = false;
    }
        
    isComponentDisabled(): boolean {
        return this.isLoading || this.isChartLoading || this.budgetService.isTabInitialLoadingSubject;
    }
}
